import { createBrowserRouter, Navigate } from 'react-router-dom';
import {
    AddBroadcastPage,
    AddEmailPage,
    AddFlowPage,
    AffiliateAnalyticsPage,
    AffiliateLinksPage,
    AnalyticsPage,
    AppointmentDetailsPage,
    AppointmentsPage,
    AuthPage,
    CourseAnalyticsPage,
    CreateFunnelPage,
    CustomerDetailsPage,
    CustomersPage,
    DashboardPage,
    EditBroadcastPage,
    EditEmailPage,
    EmailFlowsPage,
    EmailRecipientsPage,
    ForgotPasswordPage,
    FunnelDetailPage,
    FunnelsPage,
    IncomePage,
    IntegrationAuthorizationPage,
    InternalsCalendarPage,
    InternalsChartsPage,
    InternalsComponentsPage,
    InternalsDataRowPage,
    InternalsFileUploadPage,
    InternalsNavigationConfirmationPage,
    InternalsPage,
    InternalsSegmentedControlPage,
    InternalsTabsPage,
    InternalsTipTapEditorPage,
    LoginPage,
    MoreOptionsPage,
    OauthRedirectPage,
    PaymentDetailsPage,
    ProfilePage,
    ReferralsPage,
    ResetPasswordPage,
    SettingsPage,
    StoresPage,
    SupportPage,
} from '@/pages';
import { AuthLayout, EmptyLayout, ErrorBoundary, InternalsLayout, MainLayout } from '@/components';
import { FunnelsBetaGuard } from '@/features/funnels/components';
import App from '@/App';
import {
    AdsTrackingProvider,
    AuthProvider,
    ClarityProvider,
    GoogleAnalyticsProvider,
    MiscProvider,
    StoreProvider,
    StripeProvider,
    UserflowProvider,
    UserProvider,
} from '@/providers';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/queries';
import EditProductPage from '@/pages/EditProductPage';
import AddProductPage from '@/pages/AddProductPage';
import AutomationsPage from '@/pages/AutomationsPage';
import RegisterPage from '@/pages/RegisterPage';
import SubscribeSocialsPage from '@/pages/auth/SubscribeSocialsPage';
import SubscribePlanPage from '@/pages/auth/SubscribePlanPage';
import SubscribePaymentPage from '@/pages/auth/SubscribePaymentPage';
import CompleteAffiliateRegistrationPage from '@/pages/auth/CompleteAffiliateRegistrationPage';

export const router = createBrowserRouter([
    {
        // ErrorBoundary catches hook ordering errors that can occur during logout
        // when components re-render while authentication state is changing
        errorElement: <ErrorBoundary />,
        element: (
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <UserflowProvider>
                        <ClarityProvider>
                            <GoogleAnalyticsProvider>
                                <AdsTrackingProvider>
                                    <UserProvider>
                                        <MiscProvider>
                                            <StripeProvider>
                                                <StoreProvider>
                                                    <App />
                                                </StoreProvider>
                                            </StripeProvider>
                                        </MiscProvider>
                                    </UserProvider>
                                </AdsTrackingProvider>
                            </GoogleAnalyticsProvider>
                        </ClarityProvider>
                    </UserflowProvider>
                </AuthProvider>
            </QueryClientProvider>
        ),
        children: [
            // Auth Layout Routes (no sidebar/navbar)
            {
                element: <AuthLayout />,
                children: [
                    {
                        path: '/auth',
                        element: <AuthPage />,
                    },
                    {
                        path: '/register',
                        element: <RegisterPage />,
                    },
                    {
                        path: '/login',
                        element: <LoginPage />,
                    },
                    {
                        path: '/forgot_password',
                        element: <ForgotPasswordPage />,
                    },
                    {
                        path: '/reset-password',
                        element: <ResetPasswordPage />,
                    },
                    {
                        path: '/subscribe/socials',
                        element: <SubscribeSocialsPage />,
                    },
                    {
                        path: '/subscribe/plan',
                        element: <SubscribePlanPage />,
                    },
                    {
                        path: '/subscribe/start',
                        element: <SubscribePaymentPage />,
                    },
                    {
                        path: '/complete-registration',
                        element: <CompleteAffiliateRegistrationPage />,
                    },
                ],
            },
            // Main Layout Routes (with sidebar/navbar)
            {
                element: <MainLayout />,
                children: [
                    {
                        path: '/dashboard',
                        element: <DashboardPage />,
                    },
                    {
                        path: '/profile',
                        element: <ProfilePage />,
                    },
                    {
                        path: '/account_settings',
                        element: <SettingsPage />,
                    },
                    {
                        path: '/stores',
                        children: [
                            {
                                index: true,
                                element: <StoresPage />,
                            },
                            {
                                path: ':storeId/page/create',
                                element: <AddProductPage />,
                            },
                            {
                                path: ':storeId/page/:pageType/create',
                                element: <EditProductPage />,
                            },
                            {
                                path: ':storeId/page/:pageType/:pageId',
                                element: <EditProductPage />,
                            },
                        ],
                    },
                    {
                        path: '/flows',
                        children: [
                            {
                                index: true,
                                element: <EmailFlowsPage />,
                            },
                            {
                                path: 'new',
                                element: <AddFlowPage />,
                            },
                            {
                                path: ':flowId/new',
                                element: <AddEmailPage />,
                            },
                            {
                                path: ':flowId/emails/:emailId',
                                element: <EditEmailPage />,
                            },
                            {
                                path: '-1/broadcast/new',
                                element: <AddBroadcastPage />,
                            },
                            {
                                path: ':flowId/broadcast/emails/:emailId',
                                element: <EditBroadcastPage />,
                            },
                            {
                                path: 'recipients/:emailId',
                                element: <EmailRecipientsPage />,
                            },
                            {
                                path: 'recipients/:emailId/:page?',
                                element: <EmailRecipientsPage />,
                            },
                        ],
                    },
                    {
                        // Funnels V2 is gated behind the `store_funnel_v2` experiment.
                        // Users without the flag get redirected to legacy Vue admin by
                        // FunnelsBetaGuard. Users with the flag render the React v2 UI.
                        // Mirror: Vue router's beforeEach pushes flagged users in the other
                        // direction when they hit /funnels on admin.stan.store.
                        path: '/funnels',
                        element: <FunnelsBetaGuard />,
                        children: [
                            {
                                index: true,
                                element: <FunnelsPage />,
                            },
                            {
                                path: 'create',
                                element: <CreateFunnelPage />,
                            },
                            {
                                path: ':storeId',
                                element: <FunnelDetailPage />,
                            },
                        ],
                    },
                    {
                        path: '/automations',
                        element: <AutomationsPage />,
                    },
                    {
                        path: '/income',
                        children: [
                            {
                                index: true,
                                element: <IncomePage />,
                            },
                            {
                                path: 'details/:paymentId',
                                element: <PaymentDetailsPage />,
                            },
                        ],
                    },
                    {
                        path: '/audience',
                        children: [
                            {
                                index: true,
                                element: <CustomersPage />,
                            },
                            {
                                path: 'details/:userFanId',
                                element: <CustomerDetailsPage />,
                            },
                        ],
                    },
                    {
                        path: '/analytics',
                        children: [
                            {
                                index: true,
                                element: <AnalyticsPage />,
                            },
                            {
                                path: 'affiliate/:pageId',
                                element: <AffiliateAnalyticsPage />,
                            },
                            {
                                path: 'course/:courseId',
                                element: <CourseAnalyticsPage />,
                            },
                        ],
                    },
                    {
                        path: '/affiliate',
                        element: <AffiliateLinksPage />,
                    },
                    {
                        path: '/appointments',
                        children: [
                            {
                                path: ':view?',
                                element: <AppointmentsPage />,
                            },
                            {
                                path: 'details/:bookingId',
                                element: <AppointmentDetailsPage />,
                            },
                        ],
                    },
                    {
                        path: '/referrals',
                        element: <ReferralsPage />,
                    },
                    {
                        path: '/apps',
                        element: <MoreOptionsPage />,
                    },
                    // Internals nested layout
                    {
                        path: '/internals',
                        element: <InternalsLayout />,
                        children: [
                            {
                                index: true,
                                element: <InternalsPage />,
                            },
                            {
                                path: 'charts',
                                element: <InternalsChartsPage />,
                            },
                            {
                                path: 'calendar',
                                element: <InternalsCalendarPage />,
                            },
                            {
                                path: 'components',
                                element: <InternalsComponentsPage />,
                            },
                            {
                                path: 'data-row',
                                element: <InternalsDataRowPage />,
                            },
                            {
                                path: 'tiptap-editor',
                                element: <InternalsTipTapEditorPage />,
                            },
                            {
                                path: 'segmented-control',
                                element: <InternalsSegmentedControlPage />,
                            },
                            {
                                path: 'tabs',
                                element: <InternalsTabsPage />,
                            },
                            {
                                path: 'navigation-confirmation',
                                element: <InternalsNavigationConfirmationPage />,
                            },
                            {
                                path: 'unsplash-image-selector',
                                element: <InternalsFileUploadPage />,
                            },
                        ],
                    },
                ],
            },
            {
                path: '/support',
                element: <EmptyLayout />,
                children: [
                    {
                        index: true,
                        element: <SupportPage />,
                    },
                ],
            },
            // Integration Authorization (no layout)
            {
                path: '/integration-authorization',
                element: <IntegrationAuthorizationPage />,
            },

            // Stripe Connect OAuth callback — handler lives in Vue admin, bounce there.
            {
                path: '/oauth',
                element: <OauthRedirectPage />,
            },

            // Catch-all redirect
            {
                path: '*',
                element: <Navigate to='/dashboard' />,
            },
        ],
    },
]);
